import { ChangeDetectorRef, Component, EventEmitter, Output, ViewEncapsulation } from "@angular/core";
import { endOfDay, isSameMonth, isWithinInterval, startOfDay } from "@vattenfall/util";

import { CalendarDirective, DateRange } from "../calendar.directive";

@Component({
  selector: "ui-month",
  styleUrls: ["./month.component.scss"],
  templateUrl: "./month.component.html",
  encapsulation: ViewEncapsulation.Emulated,
  host: {
    "[class.ds-invisible]": "hidden",
  },
})
export class MonthComponent extends CalendarDirective {
  @Output()
  readonly selectDayEvent: EventEmitter<DateRange> = new EventEmitter<DateRange>();
  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  /**
   * Selects a day from the calendar view.
   * @param day - the day to select
   */
  select(day: Date): void {
    this.selectDayEvent.emit(new DateRange(day));
  }

  /**
   *
   * @param date - returns true if the date is not in the same month as the current date
   */
  notInSameMonth(date: Date) {
    const dateToCompare = this.selected.start || new Date();
    return !isSameMonth(date, dateToCompare);
  }

  /**
   *
   * @param date - compares if two dates are the same
   */
  isSelected(date: Date): boolean {
    if (!this.isValidRange(this.selected)) return false;
    return isWithinInterval(date, this.selected);
  }
  /**
   * This function will check if a given date needs to be removed from the view. For example if the date is not part of the month we are currently showing.
   * @param date - checks if the current view needs to hide a given date from the calendar.
   * @returns - boolean - true if its not in the same month
   */
  isHidden(date: Date) {
    const active = this.active && this.active.start ? this.active.start : new Date();
    return !isSameMonth(date, active);
  }

  /**
   * This function checks if a given date should be disabled.
   * It is disabled if the date is outside the min/max range or within the disabledDateRanges.
   * @param date - The date to check for being outside the min/max scope or within disabledDateRanges.
   * @returns a boolean value.
   */
  isDisabled(date: Date) {
    const isOutsideMinAndMaxRange = !isWithinInterval(date, { start: startOfDay(this.min), end: startOfDay(this.max) });
    const isDateDisabledInRanges = this.disabledDateRanges.some((range: DateRange) =>
      isWithinInterval(date, { start: startOfDay(range.start), end: startOfDay(range.end) })
    );

    return isDateDisabledInRanges || isOutsideMinAndMaxRange;
  }

  /**
   * Emit the newly selected day to the parent element
   * @param day - the selected date to be emmited
   */
  onSelectedChange(day: Date) {
    const selectedMonth = new DateRange(startOfDay(day), endOfDay(day));
    this.selectDayEvent.emit(selectedMonth);
  }
}
